// Enter vitals: one form that completes the entry (`RPM-098`, `RPM-100`, `CR-44`, `J-532`).
//
// "Every entry is completed on Enter vitals, and there is one way to save it." The route composes
// the features the screen is made of, in `RPM-098`'s tab order: the building (`RPM-096`), the resident
// (`RPM-097`), the care unit (`RPM-031`), then the vitals form — the date and time and the capture
// rows — and, once the entry's first store has made its completion record, the completion sections
// under the vitals: the details and the note, who to notify, the communication steps and Complete
// entry. A feature never imports another feature, so they meet here. Above them all, Not finished
// lists every entry left before Complete entry, and resume opens one where it was left (`J-533`).
//
// THE ADDRESS CARRIES THE OPEN ENTRY (`D-1055`, moved here from the worklist): `?alertId=<uuid>`, the
// entry's completion record, an opaque id and nothing about the resident. A reload, a session lock or
// a bookmark returns to the same entry, read back through `GET /api/rpm/v1/entries/:rpmAlertId`.
//
// The path is a decision (`D-816`): `vitals-entry` names what the screen is for in `RPM-098`'s words.
import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useCanGoBack, useLocation, useRouter } from "@tanstack/react-router";
import { z } from "zod";
import type { ResidentSummary } from "@chartvn/schema";
import { Button } from "@chartvn/ui/button";
import { ResidentBanner } from "@chartvn/ui/components/resident-banner/resident-banner";
import { ResidentStatusWarning } from "@chartvn/ui/components/resident-status-warning/resident-status-warning";
import { Can } from "../../features/auth/can";
import { useSession } from "../../features/auth/client";
import { usePermission } from "../../features/auth/use-permission";
import {
  heldCallOfferQueryOptions,
  heldCallsQueryOptions,
  holdsACall,
} from "../../features/entry-completion/api/held-calls";
import { refreshEntryCompletion } from "../../features/entry-completion/api/refresh";
import { CareUnitLine } from "../../features/entry-completion/components/care-unit-line";
import { EntryCommunication } from "../../features/entry-completion/components/entry-communication";
import { EntryDetails } from "../../features/entry-completion/components/entry-details";
import { EntryOwedLine } from "../../features/entry-completion/components/entry-owed-line";
import { FacilityChangeHeldDialog } from "../../features/entry-completion/components/facility-change-held-dialog";
import { HeldCallPanel } from "../../features/entry-completion/components/held-call-panel";
import { useEntryFacts } from "../../features/entry-completion/hooks/use-entry-facts";
import { useOxygenAnswer } from "../../features/entry-completion/hooks/use-oxygen-answer";
import { WORK_AN_ALERT } from "../../features/entry-completion/workup-permissions";
import { BuildingContact } from "../../features/facility-pick/building-contact";
import { FacilityChooser } from "../../features/facility-pick/facility-chooser";
import { facilityPickQueryOptions } from "../../features/facility-pick/api/facilities";
import { pickResident } from "../../features/resident-pick/api/residents";
import { ResidentChooser } from "../../features/resident-pick/resident-chooser";
import { ageOn, bannerMirrorContext } from "../../features/resident-pick/resident-detail";
import { openedEntryQueryOptions, type OpenedEntry } from "../../features/vitals-entry/api/entries";
import { IdentityCorrectionDialog } from "../../features/vitals-entry/components/identity-correction-dialog";
import { refreshNotFinished } from "../../features/vitals-entry/api/not-finished";
import { NotFinished } from "../../features/vitals-entry/components/not-finished";
import { EntryTime } from "../../features/vitals-entry/components/entry-time";
import { EntryHistoryPanel } from "../../features/entry-history/components/entry-history-panel";
import { EntryViews } from "../../features/entry-history/components/entry-views";
import type { SavedOxygen } from "../../features/vitals-entry/components/corrected-readings";
import { StoredReadings } from "../../features/vitals-entry/components/stored-readings";
import {
  reenteredEntry,
  restoredEntry,
  type RestoredEntry,
} from "../../features/vitals-entry/restore";
import { VitalsForm } from "../../features/vitals-entry/vitals-form";

/** The anchor the worklist opens an entry at: its communication section (`RPM-004`, `J-535`). */
const COMMUNICATION_ANCHOR = "entry-communication";

/**
 * The building to start at, whether this is "next resident" (`RPM-089`), the open entry, and whether
 * the worklist opened it. Every value is a mirror code, a flag or a UUID: a resident is never carried
 * in the address (`.claude/rules/phi.md` 2).
 */
const searchSchema = z.object({
  facilityId: z.string().min(1).max(20).optional(),
  next: z.boolean().optional(),
  alertId: z.uuid().optional(),
  from: z.literal("worklist").optional(),
  /** The entry's History tab is open (`RPM-018`, `J-290`): tabs are addresses (`AR-2`). */
  view: z.literal("history").optional(),
  /** A band set opened from the History tab's Version in effect rows (`RPM-056`). */
  bandSetId: z.uuid().optional(),
});

export const Route = createFileRoute("/_authenticated/vitals-entry")({
  // The shell reads this for the page heading and the document title (22-COMPOUNDS-SHELL §2.1).
  staticData: { screenName: "Enter vitals" },
  validateSearch: searchSchema,
  component: VitalsEntryPage,
});

/** A date as the banner shows it: the day, never the instant. Same rule as the option line. */
const day = (value: string | null): string => (value ? value.slice(0, 10) : "");

/**
 * The mirror record the chooser picked, in the shape the banner takes (`I-2`: two identifiers, the
 * age formatted into the birth date by `resident-detail.ts`'s `ageOn`). NO MRN, EVER (`RPM-097`).
 */
function toBannerRecord(r: ResidentSummary) {
  const age = ageOn(r.birthDate, new Date());
  return {
    name: r.name ?? "Name not recorded",
    ...(r.birthDate ? { birthDate: `${day(r.birthDate)}${age === null ? "" : ` (${age})`}` } : {}),
    ...(r.gender ? { gender: r.gender } : {}),
    ...(r.unit ? { unit: r.unit } : {}),
    ...(r.room ? { room: r.room } : {}),
    ...(r.bed ? { bed: r.bed } : {}),
  };
}

/** Where a screen starts: nothing, a kept building, or an entry reopened from its address. */
interface EntryStart {
  facilityId?: string | undefined;
  resident?: ResidentSummary;
  careUnitId?: string | undefined;
  alertId?: string;
  submissionKey?: string;
  /** The form's state for a reopened entry the form can store again. */
  restored?: RestoredEntry;
  /** A reopened entry the form cannot store again: shown and corrected only. */
  storedOnly?: boolean;
}

interface Screen {
  /** Bumped for every new entry, which remounts the screen with nothing carried over. */
  generation: number;
  start: EntryStart;
  /**
   * Every completion record this screen's entry has stood on: the first store's, and one an identity
   * correction or a correction opened. An address naming one of them is this entry, whether or not
   * the address has caught up with the latest; any other is another entry, read back.
   */
  owned: readonly string[];
  /** The address has named this entry; an address naming none after that is a new entry. */
  seen: boolean;
}

function VitalsEntryPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const [screen, setScreen] = useState<Screen>({
    generation: 0,
    start: { facilityId: search.facilityId },
    owned: [],
    seen: false,
  });
  const ours = search.alertId !== undefined && screen.owned.includes(search.alertId);

  // THE ADDRESS AND THE SCREEN AGREE, adjusted while rendering rather than in an effect: the address
  // caught up with the stored entry, or it moved on — next resident, the side nav, the back button —
  // to a new entry.
  if (ours && !screen.seen) setScreen({ ...screen, seen: true });
  if (screen.seen && search.alertId === undefined)
    setScreen({
      generation: screen.generation + 1,
      start: { facilityId: search.facilityId },
      owned: [],
      seen: false,
    });

  if (search.alertId !== undefined && !ours) {
    const asked = search.alertId;
    return (
      <EntryLoader
        key={asked}
        alertId={asked}
        onLoaded={(start) => {
          const current = start.alertId ?? asked;
          setScreen({
            generation: screen.generation + 1,
            start,
            owned: current === asked ? [asked] : [asked, current],
            seen: false,
          });
          // A correction may have moved the entry to a newer record: the address follows it.
          if (current !== asked)
            void navigate({ search: (prev) => ({ ...prev, alertId: current }), replace: true });
        }}
      />
    );
  }

  return (
    <EntryScreen
      key={screen.generation}
      start={screen.start}
      next={search.next === true && search.facilityId !== undefined}
      keptFacilityId={search.facilityId}
      fromWorklist={search.from === "worklist"}
      history={search.view === "history"}
      bandSetId={search.bandSetId}
      onAlert={(alertId) => {
        setScreen((held) => ({ ...held, owned: [...held.owned, alertId] }));
        void navigate({ search: (prev) => ({ ...prev, alertId }), replace: true });
      }}
    />
  );
}

/** What a reopened entry starts the screen with. */
function startOf(entry: OpenedEntry, resident: ResidentSummary): EntryStart {
  const restored = restoredEntry(entry);
  return {
    facilityId: entry.facilityId,
    resident,
    careUnitId: entry.careUnitId ?? undefined,
    alertId: entry.rpmAlertId,
    ...(restored === null
      ? { storedOnly: true }
      : { restored, submissionKey: restored.submissionKey }),
  };
}

interface EntryLoaderProps {
  alertId: string;
  onLoaded: (start: EntryStart) => void;
}

/**
 * Reads the entry behind the address, then picks its resident again — which is what records that her
 * record was reached (`RPM-053`), as a pick on the chooser does — and hands the screen its start.
 */
function EntryLoader({ alertId, onLoaded }: EntryLoaderProps) {
  const entry = useQuery(openedEntryQueryOptions(alertId));
  const pick = useMutation({ mutationFn: pickResident });
  const { mutate } = pick;
  const loaded = useRef(onLoaded);
  // ONCE PER ENTRY, EVEN WHERE REACT RUNS AN EFFECT TWICE: each pick writes an access row.
  const picked = useRef(false);
  useEffect(() => {
    loaded.current = onLoaded;
  });
  const opened = entry.data;
  useEffect(() => {
    if (opened === undefined || picked.current) return;
    picked.current = true;
    mutate(
      {
        facilityId: opened.facilityId,
        careasePatientId: opened.careasePatientId,
        includeDeparted: true,
      },
      { onSuccess: (resident) => loaded.current(startOf(opened, resident)) },
    );
  }, [opened, mutate]);
  if (entry.error || pick.error)
    return (
      <p data-testid="vitals-entry-open-failed" role="alert" className="p-4 text-sm">
        This entry could not be opened. It may be at a building this account does not reach.
      </p>
    );
  return (
    <p data-testid="vitals-entry-opening" role="status" className="p-4 text-sm">
      Opening the entry…
    </p>
  );
}

interface EntryScreenProps {
  start: EntryStart;
  /** "Next resident" brought her here (`RPM-089`): the resident chooser takes focus. */
  next: boolean;
  /** The building "next resident" kept, from the address. */
  keptFacilityId: string | undefined;
  /** The worklist opened this entry, so Back to worklist returns to it as she left it. */
  fromWorklist: boolean;
  /** The entry's completion record, from the first store on, and after any correction that moves it. */
  onAlert: (alertId: string) => void;
  /** The History tab is the view the address names (`RPM-018`, `J-290`). */
  history: boolean;
  /** A band set opened from the History tab (`RPM-056`). */
  bandSetId: string | undefined;
}

function EntryScreen({
  start,
  next,
  keptFacilityId,
  fromWorklist,
  onAlert,
  history,
  bandSetId,
}: EntryScreenProps) {
  const client = useQueryClient();
  const router = useRouter();
  const canGoBack = useCanGoBack();
  // OPENED FROM THE WORKLIST AT THE COMMUNICATION SECTION (`RPM-004`'s Keyboard row): focus goes
  // there, and the resident and the building are announced.
  const hash = useLocation({ select: (l) => l.hash });
  const atCommunication = fromWorklist && hash === COMMUNICATION_ANCHOR;
  const [facilityId, setFacilityId] = useState<string | undefined>(start.facilityId);
  const [resident, setResident] = useState<ResidentSummary | undefined>(start.resident);
  const [careUnitId, setCareUnitId] = useState<string | undefined>(start.careUnitId);
  const [alertId, setAlertId] = useState(start.alertId ?? "");
  /** The entry's key (`RPM-005`), minted when the entry begins; new only for an identity correction. */
  const [submissionKey, setSubmissionKey] = useState(
    () => start.submissionKey ?? crypto.randomUUID(),
  );
  /** Remounts the form, and so empties it, for a new entry. */
  const [formGeneration, setFormGeneration] = useState(0);
  /** What the form opens with: the reopened entry, or the one entered again against a re-pick. */
  const [formStart, setFormStart] = useState<RestoredEntry | undefined>(start.restored);
  /** Remounts the resident chooser showing `resident` again after a cancelled re-pick. */
  const [chooserGeneration, setChooserGeneration] = useState(0);
  const [identity, setIdentity] = useState<
    { supersedesRpmAlertId: string; reason: string } | undefined
  >(undefined);
  /** A resident re-picked after the first store, waiting on the reason (`RPM-005` row 5b). */
  const [repick, setRepick] = useState<ResidentSummary | null>(null);
  const picks = useQuery(facilityPickQueryOptions());
  // `POST …/corrections` and `…/time-corrections` ask `reading: ["correct"]` (`RPM-066`).
  const canCorrect = usePermission({ reading: ["correct"] });
  // `POST /api/rpm/v1/entries` asks `reading: ["ingest"]` (`RPM-066`). An account without it that
  // opens a stored entry reads it, and is offered no vitals to type into; a new entry still reaches
  // the form and the store's refusal (`alerts/entry.spec.ts`).
  const canEnter = usePermission({ reading: ["ingest"] });
  // `GET /api/rpm/v1/entries` asks `alert: ["read"]` (`RPM-004`); Not finished is that route's list.
  const canList = usePermission({ alert: ["read"] });
  /**
   * ONE CONTROL PER FACT (`J-532`, `CR-45`). The care unit is the one control on the line at the
   * top of the entry, before and after the first store (`R4`, `J-600`); once the record exists that
   * control writes it — a change re-resolves the routing — and the form sends what the record holds
   * on every store, so a store never puts back a unit the line changed.
   */
  const facts = useEntryFacts(alertId);
  /**
   * THE OXYGEN QUESTION ON THE SpO₂ ROW ONCE THE ENTRY IS STORED (`RPM-024`, `CR-45` R2, `J-599`):
   * the row's one control answers through `POST …/oxygen-status`, and what it re-scored is read again.
   */
  const oxygen = useOxygenAnswer(alertId, () => {
    void refreshEntryCompletion(client, alertId);
    void refreshNotFinished(client);
    void client.invalidateQueries({ queryKey: openedEntryQueryOptions(alertId).queryKey });
  });
  // `POST …/oxygen-status` asks `alert: ["work"]` (`RPM-066`): a reader is shown the answer as text.
  const canAnswerOxygen = usePermission(WORK_AN_ALERT);
  /** The entry is shown and corrected, never typed into: stored before row keys, or read by a reader. */
  const storedOnly = start.storedOnly === true || (alertId !== "" && !canEnter);
  const entryCareUnitId =
    alertId !== "" && facts !== undefined ? (facts.careUnitId ?? careUnitId) : careUnitId;
  const building = picks.data?.items.find((b) => b.facilityId === facilityId);
  const zone = building?.ianaTimeZone ?? null;
  const keptName = picks.data?.items.find((b) => b.facilityId === keptFacilityId)?.name;
  // `RPM-089` row 2: a held call is kept when she moves on, and the announcement names it.
  const heldAtBuilding = useQuery(heldCallsQueryOptions(facilityId ?? "")).data ?? [];
  const me = useSession().data?.user.id;
  /** The signed-in account's name, for its own line under time on the entry (`RPM-106`). */
  const myName = useSession().data?.user.name ?? null;
  /** The open batch this entry's call is held for (`RPM-039`); a building change takes it off first. */
  const heldOn = useQuery(heldCallOfferQueryOptions(alertId)).data?.heldOn ?? null;
  /** A building picked while the entry is on a held call, waiting on the confirmation (`CR-4`). */
  const [pendingFacility, setPendingFacility] = useState<string | undefined>(undefined);
  const arrivalAnnouncement =
    next && keptName !== undefined && facilityId === keptFacilityId
      ? holdsACall(heldAtBuilding, me)
        ? `Next resident. Still at ${keptName}, and the held call stays on Calls waiting; nothing else is kept.`
        : `Next resident. Still at ${keptName}; nothing else is kept.`
      : atCommunication && start.alertId !== undefined && building !== undefined
        ? `Opened from the worklist: ${start.resident?.name ?? "a resident not in the mirror"} at ${building.name}.`
        : "";

  /** A new entry, with nothing carried over but what the caller keeps. */
  function newEntry() {
    setAlertId("");
    setIdentity(undefined);
    setSubmissionKey(crypto.randomUUID());
    setFormStart(undefined);
    setFormGeneration((n) => n + 1);
  }

  /** A new building: a resident belongs to the building she was picked from, so a new entry. */
  function changeFacility(chosen: string | undefined) {
    setFacilityId(chosen);
    setCareUnitId(undefined);
    setResident(undefined);
    newEntry();
  }

  function stored(entryAlertId: string | undefined) {
    if (entryAlertId === undefined) return;
    void refreshEntryCompletion(client, entryAlertId);
    void refreshNotFinished(client);
    if (entryAlertId === alertId) return;
    setAlertId(entryAlertId);
    onAlert(entryAlertId);
  }

  /** The History tab shows once the entry has a record to have a history (`J-290`). */
  const showHistory = history && alertId !== "";

  return (
    // THE ENTRY STAYS MOUNTED UNDER THE HISTORY TAB (`11-` §1.18: "unsaved panel state survives
    // switching", `NL-12`): with the History tab open every section but the top bar, the two view
    // links and the history itself is hidden, never unmounted, so nothing typed is lost.
    <div
      data-view={showHistory ? "history" : "entry"}
      className="mx-auto w-full max-w-5xl space-y-6 p-4 [&[data-view=history]>*:not([data-entry-keep])]:hidden"
    >
      {/* THE WAY OUT STAYS IN VIEW (`J-557`): an entry opened from the worklist scrolls to its
          communication section, and the owner was stuck there. Whatever the state, this bar says what
          is owed and the one action that releases it, and offers the worklist and another resident. */}
      <div
        data-testid="entry-top-bar"
        data-entry-keep
        className="bg-background sticky top-0 z-10 flex flex-col gap-2 border-b py-2"
      >
        <div className="flex flex-wrap gap-2">
          {/* `RPM-004`'s Back to worklist: to the worklist as she left it, filters and all. */}
          <Button
            type="button"
            variant="outline"
            data-testid="back-to-worklist"
            className="min-h-11"
            onClick={() => {
              if (fromWorklist && canGoBack) router.history.back();
              else void router.navigate({ to: "/worklist" });
            }}
          >
            Back to worklist
          </Button>
          {alertId === "" ? null : (
            <Button
              type="button"
              variant="outline"
              data-testid="another-resident"
              className="min-h-11"
              onClick={() => {
                // The side nav's Enter vitals: a new entry, nothing of this one kept on the screen.
                void router.navigate({ to: "/vitals-entry", search: {} });
              }}
            >
              Another resident
            </Button>
          )}
          {/* TIME ON THE ENTRY, UPPER RIGHT (`RPM-106`, `CR-45`, `J-601`): it counts while a resident
              is picked and this account may work the entry — its tick asks `alert: ["work"]`, as the
              oxygen status does — and holds the time until the first store makes the record. */}
          {facilityId && resident ? (
            <EntryTime
              className="ml-auto"
              entryKey={submissionKey}
              alertId={alertId}
              open={canAnswerOxygen}
              userId={me}
              userName={myName}
              zone={zone}
            />
          ) : null}
        </div>
        <EntryOwedLine alertId={alertId} />
      </div>
      {alertId === "" ? null : (
        <div data-entry-keep>
          <EntryViews view={showHistory ? "history" : "entry"} />
        </div>
      )}
      {showHistory ? (
        <div data-entry-keep className="space-y-6">
          {/* THE BANNER STAYS ON SCREEN (`SI-1`): the history is about this resident, and the entry's
              own banner is among the sections the History tab hides. */}
          {resident ? (
            <ResidentBanner
              key={resident.careasePatientId}
              data-testid="entry-history-banner"
              record={toBannerRecord(resident)}
              mirrorContext={bannerMirrorContext(resident)}
            />
          ) : null}
          <EntryHistoryPanel alertId={alertId} zone={zone} bandSetId={bandSetId} />
        </div>
      ) : null}
      {/* NOT FINISHED, AT THE TOP (`RPM-089`, `CR-44`, `J-533`): every entry left before Complete
          entry, in every building this account reaches; resume opens it where it was left. */}
      {canList ? (
        <NotFinished
          currentAlertId={alertId}
          zoneOf={(id) => picks.data?.items.find((b) => b.facilityId === id)?.ianaTimeZone ?? null}
          onResume={(id) => {
            void router.navigate({ to: "/vitals-entry", search: { alertId: id } });
          }}
        />
      ) : null}
      <p data-testid="vitals-entry-next" role="status" className="text-sm">
        {arrivalAnnouncement}
      </p>
      {/* THE LINE AT THE TOP OF THE ENTRY (`RPM-022`, `RPM-098`, `CR-45` R4, `J-600`): the building, the
          resident, and the care unit with where the resident was — one control for the unit, before
          and after the first store, and no separate location section. */}
      <CareUnitLine
        gate={Can}
        facility={
          <FacilityChooser
            value={facilityId}
            onChange={(chosen) => {
              // ON A HELD CALL, THE ENTRY IS TAKEN OFF IT FIRST, with confirmation (`CR-4`, `RPM-039`).
              if (heldOn !== null && chosen !== facilityId) {
                setPendingFacility(chosen);
                return;
              }
              changeFacility(chosen);
            }}
          />
        }
        resident={
          facilityId && !storedOnly ? (
            <ResidentChooser
              key={`chooser:${facilityId}:${String(chooserGeneration)}`}
              facilityId={facilityId}
              initialValue={resident?.careasePatientId}
              onPicked={(picked) => {
                if (picked.careasePatientId === resident?.careasePatientId) return;
                // AFTER THE FIRST STORE A RE-PICK IS AN IDENTITY CORRECTION, asked for with a reason.
                if (alertId !== "" && resident !== undefined) {
                  setRepick(picked);
                  return;
                }
                setResident(picked);
                // A new resident is picked with her own unit, never the previous resident's (`NE-01`).
                setCareUnitId(undefined);
                newEntry();
              }}
              onCleared={() => {
                // The departed resident the toggle added left the list: the form holds nobody, until
                // an entry is stored, when a change of resident is an identity correction instead.
                if (alertId !== "") return;
                setResident(undefined);
                setCareUnitId(undefined);
              }}
              takeFocus={next && facilityId === keptFacilityId}
            />
          ) : null
        }
        facilityId={facilityId}
        alertId={alertId}
        suggestedCareUnitId={resident?.suggestedCareUnitId ?? null}
        residentPicked={resident !== undefined}
        value={entryCareUnitId}
        onChange={setCareUnitId}
      />
      {pendingFacility === undefined || heldOn === null ? null : (
        <FacilityChangeHeldDialog
          alertId={alertId}
          batch={heldOn}
          onDetached={() => {
            changeFacility(pendingFacility);
            setPendingFacility(undefined);
          }}
          onCancel={() => {
            setPendingFacility(undefined);
          }}
        />
      )}
      {/* `RPM-085`: BEFORE ANY CONTACT IS OFFERED — ahead of the building's contact instruction, the
          calls waiting and Who to notify, so the contact controls come after it in tab order. Rendered
          for the building, not keyed to the resident, so the alert region already exists when a
          discharged or deceased resident is picked and her warning is announced (`03-` §5.2 rule 3).
          It refuses nothing: the reading stores and the obligation stays (`J-307`). */}
      {facilityId ? (
        <ResidentStatusWarning
          data-testid="resident-status-warning"
          status={resident?.statusWarning?.kind ?? "CURRENT"}
          {...(resident?.statusWarning?.date ? { date: day(resident.statusWarning.date) } : {})}
        />
      ) : null}
      <BuildingContact facilityId={facilityId} />
      {/* The calls waiting at this building (`RPM-039`): kept when she moves on (`RPM-089` row 2). */}
      {facilityId ? <HeldCallPanel gate={Can} facilityId={facilityId} zone={zone} /> : null}

      {/* THE IDENTITY BANNER (`NE-01`, `J-461`), replaced rather than mutated on a change of resident. */}
      {resident ? (
        <ResidentBanner
          key={resident.careasePatientId}
          data-testid="resident-banner"
          record={toBannerRecord(resident)}
          mirrorContext={bannerMirrorContext(resident)}
        />
      ) : null}

      {repick === null || resident === undefined ? null : (
        <IdentityCorrectionDialog
          // THE RESIDENT SHE IS CHANGING TO, AS THE BANNER WOULD SHOW HER (`NE-01`, `J-704`): the
          // record the banner takes, so the dialog tells two residents of one name apart.
          resident={toBannerRecord(repick)}
          onConfirm={(reason) => {
            setIdentity({ supersedesRpmAlertId: alertId, reason });
            // THE NEW ENTRY HAS NO RECORD UNTIL ITS FIRST STORE: the wrong entry's sections leave the
            // screen, and the form asks the care unit and the oxygen status again.
            setAlertId("");
            const key = crypto.randomUUID();
            setSubmissionKey(key);
            // THE NEW ENTRY CARRIES WHAT STANDS on the wrong one, corrections included.
            const wrong = client.getQueryData(openedEntryQueryOptions(alertId).queryKey);
            if (wrong !== undefined) {
              setFormStart(reenteredEntry(wrong, key));
              setFormGeneration((n) => n + 1);
            }
            setResident(repick);
            setCareUnitId(undefined);
            setRepick(null);
          }}
          onCancel={() => {
            setRepick(null);
            setChooserGeneration((n) => n + 1);
          }}
        />
      )}

      {facilityId && resident && storedOnly ? (
        <StoredOnlyEntry
          alertId={alertId}
          facilityId={facilityId}
          facilityName={building?.name ?? "this building"}
          zone={zone}
          canCorrect={canCorrect}
          focusOnArrival={atCommunication}
          oxygen={{
            value: oxygen.value,
            blockingReason: oxygen.blockingReason,
            refusal: oxygen.refusal,
            ...(canAnswerOxygen ? { onChange: oxygen.answer } : {}),
          }}
          onCorrected={(id) => stored(id)}
        />
      ) : null}

      {facilityId && resident && !storedOnly ? (
        <VitalsForm
          key={`form:${facilityId}:${String(formGeneration)}`}
          facilityId={facilityId}
          careasePatientId={resident.careasePatientId}
          careUnitId={entryCareUnitId}
          record={
            facts === undefined ? undefined : { oxygenStatus: facts.oxygenStatus, lane: facts.lane }
          }
          oxygenAnswer={oxygen}
          zone={zone}
          submissionKey={submissionKey}
          alertId={alertId}
          restored={formStart}
          identityCorrection={identity}
          canCorrect={canCorrect}
          onStored={(entry) => stored(entry.alert?.rpmAlertId)}
          onCorrected={(id) => stored(id)}
          {...(alertId === ""
            ? {}
            : {
                details: <EntryDetails gate={Can} alertId={alertId} />,
                communication: (hold: string | null) => (
                  <EntryCommunication
                    gate={Can}
                    alertId={alertId}
                    facilityName={building?.name ?? "this building"}
                    hold={hold}
                    focusOnArrival={atCommunication}
                    zone={zone}
                    // STORED ON THIS SCREEN, not opened by its address: R6 joins without a prompt.
                    autoJoin={start.alertId === undefined}
                    onNextResident={() => {
                      void router.navigate({
                        to: "/vitals-entry",
                        search: { facilityId, next: true },
                      });
                    }}
                  />
                ),
              })}
        />
      ) : null}
    </div>
  );
}

interface StoredOnlyEntryProps {
  alertId: string;
  facilityId: string;
  facilityName: string;
  zone: string | null;
  canCorrect: boolean;
  /** The worklist opened this entry at its communication section. */
  focusOnArrival: boolean;
  /** The oxygen status, on the SpO₂ row of what is stored (`CR-45` R2, `J-599`). */
  oxygen: SavedOxygen;
  /** A correction was stored, naming the entry's record: the same one, or one it opened. */
  onCorrected: (alertId: string) => void;
}

/**
 * An entry the form cannot store again — stored before row keys, or opened over a recorded
 * observation — shown with its readings, corrected one reading or its date and time at a time, and
 * completed like any other (`RPM-101`, `J-532`).
 */
function StoredOnlyEntry({
  alertId,
  facilityId,
  facilityName,
  zone,
  canCorrect,
  focusOnArrival,
  oxygen,
  onCorrected,
}: StoredOnlyEntryProps) {
  const router = useRouter();
  return (
    <section data-testid="vitals-form" aria-label="Enter vitals" className="space-y-4">
      <p data-testid="stored-only" className="text-muted-foreground text-sm">
        {canCorrect
          ? "This entry’s vitals are changed by correcting them."
          : "This entry’s vitals are shown as they are stored."}
      </p>
      <StoredReadings
        alertId={alertId}
        zone={zone}
        canCorrect={canCorrect}
        oxygen={oxygen}
        onCorrected={(_superseded, rpmAlertId) => {
          onCorrected(rpmAlertId ?? alertId);
        }}
      />
      <EntryDetails gate={Can} alertId={alertId} />
      <EntryCommunication
        gate={Can}
        alertId={alertId}
        facilityName={facilityName}
        focusOnArrival={focusOnArrival}
        zone={zone}
        onNextResident={() => {
          void router.navigate({ to: "/vitals-entry", search: { facilityId, next: true } });
        }}
      />
    </section>
  );
}
